/**
 * NPT-2004 — what the traveler is told about where the margin goes.
 *
 * The API decides it once (npt-api `utils/presentation.util`): the org payload carries the
 * org-level `presentation` (landing, FAQ, share copy) and every priced rate carries its own
 * `commission.presentation` (cards, rate modal, checkout), with the $5 per night per room saving
 * threshold already applied. A booking stores the one the traveler was shown.
 *
 * Traveler surfaces read these through `usePresentation` / `getRatePresentation` and pick copy by
 * `copyVariant`. No other code may decide whether proceeds or savings are shown. The discount
 * itself is price, not presentation: it lives in pricing.util and applies whatever the copy says.
 */

export type TravelerView = 'PROCEEDS' | 'COST_SAVINGS' | 'NOTHING';

export type CopyVariant = 'proceeds' | 'savings' | 'neutral';

export const COPY_VARIANTS: readonly CopyVariant[] = ['proceeds', 'savings', 'neutral'];

/** The org-level block on the organization payload. */
export type OrganizationPresentationType = {
   view: TravelerView;
   /** The traveler's share of the margin, in percent. */
   sharePct: number;
   copyVariant: CopyVariant;
};

/**
 * The rate-level block on `commission.presentation` and on `booking.metadata.presentation`. It has
 * no traveler share: the API keeps the margin split off anything a traveler receives.
 */
export type RatePresentationType = Omit<OrganizationPresentationType, 'sharePct'> & {
   /** Dollars the traveler saves, or null when the saving is not advertised. */
   advertisedSavingAmount: number | null;
   /** Dollars raised for the organization, or null when proceeds are not advertised. */
   advertisedProceedsAmount: number | null;
};

/** What a surface needs: the block plus the two questions every surface asks. */
export type Presentation = RatePresentationType & {
   /** The traveler share; org-level only (the org payload already carries the split). */
   sharePct?: number;
   /** Proceeds copy may render. On a rate, only when it raises a non-zero amount. */
   showProceeds: boolean;
   /** Savings copy may render. On a rate, only when the saving is advertised (may coexist with proceeds). */
   showSavings: boolean;
};

const isTravelerView = (value: unknown): value is TravelerView => value === 'PROCEEDS' || value === 'COST_SAVINGS' || value === 'NOTHING';

const VARIANT_OF: Record<TravelerView, CopyVariant> = { PROCEEDS: 'proceeds', COST_SAVINGS: 'savings', NOTHING: 'neutral' };

/**
 * The fallback when a payload carries no presentation (a payload cached from before the API
 * shipped it, a not-yet-loaded org): neutral, so nothing ever claims money went anywhere.
 */
export const NEUTRAL_PRESENTATION: Presentation = {
   view: 'NOTHING',
   sharePct: 0,
   copyVariant: 'neutral',
   advertisedSavingAmount: null,
   advertisedProceedsAmount: null,
   showProceeds: false,
   showSavings: false,
};

const amountOrNull = (value: unknown): number | null => {
   if (value == null) return null;
   const n = Number(value);
   return Number.isFinite(n) && n > 0 ? n : null;
};

/** Organization-level presentation from the org payload's `presentation` block. */
export function getOrganizationPresentation(organization?: { presentation?: Partial<OrganizationPresentationType> | null } | null): Presentation {
   const block = organization?.presentation;
   if (!block || !isTravelerView(block.view)) return NEUTRAL_PRESENTATION;
   const copyVariant = VARIANT_OF[block.view];
   return {
      view: block.view,
      sharePct: Number(block.sharePct) || 0,
      copyVariant,
      advertisedSavingAmount: null,
      advertisedProceedsAmount: null,
      showProceeds: copyVariant === 'proceeds',
      showSavings: copyVariant === 'savings',
   };
}

/**
 * Rate-level presentation from `commission.presentation` (or a booking's `metadata.presentation`).
 * Accepts the commission itself or the presentation block.
 */
export function getRatePresentation(
   source?: { presentation?: Partial<RatePresentationType> | null } | Partial<RatePresentationType> | null,
): Presentation {
   const block: Partial<RatePresentationType> | null | undefined =
      source && 'presentation' in source
         ? (source as { presentation?: Partial<RatePresentationType> | null }).presentation
         : (source as Partial<RatePresentationType>);
   if (!block || !isTravelerView(block.view)) return NEUTRAL_PRESENTATION;
   const advertisedSavingAmount = amountOrNull(block.advertisedSavingAmount);
   const advertisedProceedsAmount = amountOrNull(block.advertisedProceedsAmount);
   const copyVariant = VARIANT_OF[block.view];
   return {
      view: block.view,
      copyVariant,
      advertisedSavingAmount,
      advertisedProceedsAmount,
      showProceeds: copyVariant === 'proceeds' && advertisedProceedsAmount != null,
      showSavings: advertisedSavingAmount != null,
   };
}

/**
 * The admin live preview of a form that has not been saved yet: the same rule the API applies on
 * save (cost savings with no traveler share has nothing to advertise). Admin preview only.
 */
export function getDraftPresentation(view: unknown, sharePct: unknown): Presentation {
   const share = Number(sharePct) || 0;
   const resolved: TravelerView = !isTravelerView(view) ? 'PROCEEDS' : view === 'COST_SAVINGS' && !(share > 0) ? 'NOTHING' : view;
   return getOrganizationPresentation({ presentation: { view: resolved, sharePct: share } });
}

/** `<base>.<variant>`: the i18n key of one variant of a three-variant copy element. */
export const copyKey = (base: string, variant: CopyVariant): string => `${base}.${variant}`;

const isVariantSet = (value: unknown): value is Record<CopyVariant, unknown> =>
   !!value &&
   typeof value === 'object' &&
   !Array.isArray(value) &&
   COPY_VARIANTS.every(v => v in (value as object)) &&
   Object.keys(value as object).length === 3;

/**
 * Resolves every `{ proceeds, savings, neutral }` leaf of an i18n object (loaded with
 * `returnObjects`) to the chosen variant, so a block such as `defaultConfiguration` can carry
 * three-variant elements next to plain strings.
 */
export function resolveCopyVariants<T>(value: T, variant: CopyVariant): T {
   if (isVariantSet(value)) return (value as Record<CopyVariant, unknown>)[variant] as T;
   if (Array.isArray(value)) return value.map(item => resolveCopyVariants(item, variant)) as unknown as T;
   if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value as object).map(([key, item]) => [key, resolveCopyVariants(item, variant)])) as T;
   }
   return value;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * NPT-1803 — the share title, description and SMS text for an organization page (org landing,
 * event landing, map view), in the org's copy variant. One place, so the three share buttons
 * cannot drift apart.
 */
export function getOrganizationShareCopy(
   t: Translate,
   variant: CopyVariant,
   params: { orgName?: string; siteName?: string; supportName?: string },
): { title: string; description: string; smsText: string } {
   return {
      title: t(copyKey('organization.landing.share.title', variant), params),
      description: t(copyKey('organization.landing.share.description', variant), params),
      smsText: t(copyKey('organization.landing.share.sms', variant), params),
   };
}

/**
 * The share modal's own header, above whichever share text the page passes in. Same three
 * variants, so a cost-saving or neutral org is never told the page "helps support" it.
 */
export function getShareModalHeader(
   t: Translate,
   variant: CopyVariant,
   params: { orgName?: string; supportName?: string },
): { title: string; description: string } {
   return {
      title: t(copyKey('modal.share.title', variant), params),
      description: t(copyKey('modal.share.description', variant), params),
   };
}

/** The hotel search list view's share text and SEO title/description, in the org's copy variant. */
export function getHotelSearchShareCopy(
   t: Translate,
   variant: CopyVariant,
   params: { orgName?: string; supportName?: string; destination?: string | null; hotelCount?: number; checkIn?: string; checkOut?: string },
): { title: string; description: string; smsText: string } {
   return {
      title: t(copyKey('hotel.search.share.title', variant), params),
      description: t(copyKey('hotel.search.share.description', variant), params),
      smsText: t(copyKey('hotel.search.share.sms', variant), params),
   };
}
